import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { format } from 'date-fns';
import { CookieService } from 'ngx-cookie-service';
import {
  combineLatestWith,
  from,
  Observable,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { RecaptchaService } from '@sondermind/recaptcha';
import { SegmentService } from '@sondermind/segment';
import { CommunicationPreference } from '@sondermind/utilities/models-clinical';
import { ISessionRequestPreferences } from '@sondermind/utilities/models-scheduling';
import { SessionPreference } from '@sondermind/utilities/models-session';
import {
  IQuickCheckoutApiResponse,
  IQuickCheckoutFormValues,
  IQuickCheckoutReferralCreationRequest,
  IQuickCheckoutReferralRequestResponse,
  ISagaStatusResponse
} from '../models/quick-checkout-form.model';
import { QuickCheckoutHttpService } from '../services/quick-checkout.httpservice';
import { IPublicRequestSessionSegmentSchema } from '../models/segment/public-request-session-segment-schema.interface';

const PSL_REFERRAL_SOURCE = 'PSL Referral';
const EXTERNAL_REFERRAL = 'EXTERNAL_REFERRAL';
const EXTERNAL_REFERRAL_SEARCH = 'EXTERNAL_REFERRAL_SEARCH';

@Injectable({
  providedIn: 'root'
})
export class QuickCheckoutStore {
  // If set, then we know that a request was successfully submitted
  private referralRequestUuid: string | null = null;
  private requestedSessionPreferences: ISessionRequestPreferences | null = null;

  constructor(
    private quickCheckoutHttpService: QuickCheckoutHttpService,
    private recaptchaService: RecaptchaService,
    private segmentService: SegmentService<IPublicRequestSessionSegmentSchema>,
    private cookieService: CookieService,
    // eslint-disable-next-line @typescript-eslint/ban-types
    @Inject(PLATFORM_ID) private platformId: Object,
    private activatedRoute: ActivatedRoute
  ) {}

  /**
   * Submits the values of the quick checkout form to the data exchange service,
   * attaches the recaptcha token and segment id,
   * and handles it regardless of whether there is a requested time slot
   */
  submitQuickCheckout$(
    formValues: IQuickCheckoutFormValues,
    requestedSessionPreferences: ISessionRequestPreferences,
    source?: string,
    referringUrl?: string,
  ): Observable<IQuickCheckoutApiResponse | null> {
    return from(this.segmentService.anonymousId()).pipe(
      combineLatestWith(this.recaptchaService.executeEvent$('submit')),
      take(1),
      switchMap(([segmentAnonymousId, recaptchaToken]) => {
        const fromAIConnect = this.activatedRoute.snapshot.queryParamMap.get('fromAIConnect') === 'true';

        const body = this.createReferralRequestData(
          formValues,
          segmentAnonymousId ?? null,
          requestedSessionPreferences,
          recaptchaToken,
          fromAIConnect,
          referringUrl,
        );
        body.source_attributes.source = source ?? PSL_REFERRAL_SOURCE;
        return body.slot
          ? this.quickCheckoutHttpService.submitQuickCheckoutWithRequestedTimeSlot$(body, {})
          : this.quickCheckoutHttpService.submitQuickCheckout$(body, {});
      }),
      tap((response: IQuickCheckoutApiResponse | null) => {
        if (response?.referral_request_uuid) {
          this.setReferralRequestUuid(response.referral_request_uuid);
          this.setRequestedSessionPreferences(requestedSessionPreferences);
        }
      })
    );
  }

  createReferralRequest$(
    formValues: IQuickCheckoutFormValues,
    requestedSessionPreferences: ISessionRequestPreferences,
    referringUrl?: string,
  ): Observable<IQuickCheckoutReferralRequestResponse | null> {
    return from(this.segmentService.anonymousId()).pipe(
      combineLatestWith(this.recaptchaService.executeEvent$('submit')),
      take(1),
      switchMap(([segmentAnonymousId, recaptchaToken]) => {
        const fromAIConnect = this.activatedRoute.snapshot.queryParamMap.get('fromAIConnect') === 'true';

        const body = this.createReferralRequestData(
          formValues,
          segmentAnonymousId ?? null,
          requestedSessionPreferences,
          recaptchaToken,
          fromAIConnect,
          referringUrl,
        );
        return this.quickCheckoutHttpService.createReferralRequest$(body, {});
      }),
      tap((response: IQuickCheckoutReferralRequestResponse | null) => {
        if (response?.saga_id) {
          this.setRequestedSessionPreferences(requestedSessionPreferences);
        }
      })
    );
  }

  resendEmailVerification(): void {
    if (this.referralRequestUuid) {
      this.quickCheckoutHttpService.resendEmailVerification$(this.referralRequestUuid).subscribe();
    }
  }

  getSagaStatus$(sagaId: string): Observable<ISagaStatusResponse | null> {
    return this.quickCheckoutHttpService.getSagaStatus$(sagaId);
  }

  getSubmittedSessionRequestPreferences(): ISessionRequestPreferences | null {
    return this.requestedSessionPreferences;
  }

  /**
   * Save the response of the quick checkout so we can resend the email verification if needed
   * @param referralRequestUuid
   */
  private setReferralRequestUuid(referralRequestUuid: string): void {
    this.referralRequestUuid = referralRequestUuid;
  }

  /**
   * Save the requested session preferences so we know which time slot was submitted
   * @param requestedSessionPreferences
   */
  private setRequestedSessionPreferences(requestedSessionPreferences: ISessionRequestPreferences): void {
    this.requestedSessionPreferences = requestedSessionPreferences;
  }

  /**
   * If a referral request uuid has already been saved by the store,
   * that means at least one request has already been submitted, so
   * return true
   */
  checkIsSubmittedState(): boolean {
    return !!this.referralRequestUuid;
  }

  /**
   * Builds all of the data needed to create the referral from quick checkout
   * @param formValues the quick checkout form values
   * @param segmentAnonymousId Used in analytics
   * @param requestedSessionPreferences any details for a requested session, if they exist
   * @param token the google recaptcha token which will be verified by the BE
   * @returns IQuickCheckoutReferralCreationRequest
   */
  private createReferralRequestData(
    formValues: IQuickCheckoutFormValues,
    segmentAnonymousId: string | null,
    requestedSessionPreferences: ISessionRequestPreferences,
    token: string,
    fromAIConnect: boolean,
    referringUrl?: string,
  ): IQuickCheckoutReferralCreationRequest {
    const commPrefs: string[] = [CommunicationPreference.NCC];
    if (formValues.commPrefs.commPrefsPhone) {
      commPrefs.push(CommunicationPreference.PHONE);
    }
    if (formValues.commPrefs.commPrefsSms) {
      commPrefs.push(CommunicationPreference.SMS);
    }

    let externalReferral: string | undefined;
    let externalReferralSearch: string | undefined;
    if (isPlatformBrowser(this.platformId)) {
      externalReferral = this.cookieService.get(EXTERNAL_REFERRAL) || undefined;
      externalReferralSearch = this.cookieService.get(EXTERNAL_REFERRAL_SEARCH) || undefined;

      // If no cookie, try reading UTM params from current URL (for provider landing page flow)
      if (!externalReferralSearch) {
        const urlParams = new URLSearchParams(window.location.search);
        const utmParams = new URLSearchParams();
        urlParams.forEach((value, name) => {
          if (name.startsWith('utm') && value) {
            utmParams.append(name, value);
          }
        });

        if (utmParams.size > 0) {
          externalReferralSearch = `?${utmParams.toString()}`;
        }
      }
    }
    /* eslint-disable @typescript-eslint/naming-convention */
    // Strong casting for all these values is safe since data can only be created after
    // the form is valid, so none of these data will be null
    return {
      first_name: formValues.firstName as string,
      last_name: formValues.lastName as string,
      email: formValues.email as string,
      phone: (formValues.phone as string).replace(/[^0-9]+/g, ''), // Remove () and -'s between the digits, digits only
      received_date: new Date().toISOString(),
      slot: requestedSessionPreferences.startTime
        ? {
          start: requestedSessionPreferences.startTime?.toISOString() ?? '',
          end: requestedSessionPreferences.endTime?.toISOString() ?? ''
        }
        : undefined,
      source_attributes: {
        segment_anonymous_id: segmentAnonymousId,
        source: PSL_REFERRAL_SOURCE,
        referring_url: referringUrl,
        birthdate: format(new Date(formValues.birthDate as string), 'yyyy-MM-dd'),
        insurance: formValues.insurance as number,
        persona_provider_id: formValues.personaProviderId,
        contact_sms_allowed: formValues.contactSmsAllowed as boolean,
        comm_prefs: commPrefs,
        session_format_preference: requestedSessionPreferences.sessionType ?? SessionPreference.VIDEO,
        state: formValues.location as string,
        external_referral: externalReferral,
        external_referral_search: externalReferralSearch,
        heard_from_source: formValues.heardFrom.source,
        heard_from_details: formValues.heardFrom.details,
        from_ai_connect: fromAIConnect,
      },
      recaptcha_token: token,
    };
    /* eslint-enable @typescript-eslint/naming-convention */
  }
}
